import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BounceBackService, BounceBackBalance, BounceBackHistory } from '../../shared/services/bounceback.service';
import { UserService } from '../../shared/services/user.service';
import { NotificationService } from '../../shared/services/notification.service';
import { CompanyService } from '../../shared/services/company.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-bounceback-rewards',
  templateUrl: './bounceback-rewards.component.html',
  styleUrls: ['./bounceback-rewards.component.scss']
})
export class BouncebackRewardsComponent implements OnInit, OnDestroy {
  balance: BounceBackBalance | null = null;
  history: BounceBackHistory[] = [];
  isLoading: boolean = false;
  isLoadingHistory: boolean = false;
  errorMessage: string = '';
  selectedTab: 'overview' | 'history' = 'overview';

  private destroy$ = new Subject<void>();

  constructor(
    private bouncebackService: BounceBackService,
    private userService: UserService,
    private notificationService: NotificationService,
    private companyService: CompanyService,
    private titleService: Title,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.setPageTitle();
    this.loadBounceBackData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Set page title
   */
  private setPageTitle(): void {
    this.translate.get('global_Company_Title').subscribe((text: string) => {
      this.titleService.setTitle('BounceBack Rewards | ' + text);
    });
  }

  /**
   * Load BounceBack balance and history
   */
  loadBounceBackData(): void {
    const customerId = this.userService.userServiceModal.customerData.CustomerId;
    if (!customerId) {
      this.errorMessage = 'Customer information not available';
      return;
    }

    this.setLoading(true);
    this.loadBalance(customerId);
    this.loadHistory(customerId);
  }

  /**
   * Load BounceBack balance
   */
  private loadBalance(customerId: number): void {
    this.bouncebackService.getBounceBackBalance(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (balance: BounceBackBalance) => {
          this.balance = balance;
          this.setLoading(false);
        },
        error: (error) => {
          console.error('Error loading BounceBack balance:', error);
          this.errorMessage = 'Failed to load BounceBack balance';
          this.setLoading(false);
        }
      });
  }

  /**
   * Load BounceBack history
   */
  private loadHistory(customerId: number): void {
    this.isLoadingHistory = true;
    this.bouncebackService.getBounceBackHistory(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (history: BounceBackHistory[]) => {
          this.history = history;
          this.isLoadingHistory = false;
        },
        error: (error) => {
          console.error('Error loading BounceBack history:', error);
          this.errorMessage = 'Failed to load BounceBack history';
          this.isLoadingHistory = false;
        }
      });
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    if (loading) {
      this.errorMessage = '';
    }
  }

  /**
   * Switch between tabs
   */
  switchTab(tab: 'overview' | 'history'): void {
    this.selectedTab = tab;
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    this.loadBounceBackData();
  }

  /**
   * Get formatted balance amount
   */
  get formattedBalance(): string {
    if (!this.balance) return '$0.00';
    return this.bouncebackService.formatCurrency(
      this.balance.availableBalance, 
      this.companyService.selectedCurrency.CurrencyCode
    );
  }

  /**
   * Get formatted total earned
   */
  get formattedTotalEarned(): string {
    if (!this.balance) return '$0.00';
    return this.bouncebackService.formatCurrency(
      this.balance.totalEarned, 
      this.companyService.selectedCurrency.CurrencyCode
    );
  }

  /**
   * Get formatted total redeemed
   */
  get formattedTotalRedeemed(): string {
    if (!this.balance) return '$0.00';
    return this.bouncebackService.formatCurrency(
      this.balance.totalRedeemed, 
      this.companyService.selectedCurrency.CurrencyCode
    );
  }

  /**
   * Get formatted expiry date
   */
  get formattedExpiryDate(): string {
    if (!this.balance?.expiryDate) return 'No expiry date';
    return new Date(this.balance.expiryDate).toLocaleDateString();
  }

  /**
   * Get days until expiry
   */
  get daysUntilExpiry(): number {
    if (!this.balance?.expiryDate) return 0;
    const expiryDate = new Date(this.balance.expiryDate);
    const today = new Date();
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  /**
   * Check if balance is expiring soon (within 30 days)
   */
  get isExpiringSoon(): boolean {
    return this.daysUntilExpiry <= 30 && this.daysUntilExpiry > 0;
  }

  /**
   * Check if balance is expired
   */
  get isExpired(): boolean {
    return this.daysUntilExpiry === 0;
  }

  /**
   * Get history items filtered by type
   */
  getHistoryByType(type: 'earned' | 'redeemed' | 'expired'): BounceBackHistory[] {
    return this.history.filter(item => item.type === type);
  }

  /**
   * Get formatted date for history item
   */
  formatHistoryDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  /**
   * Get formatted amount for history item
   */
  formatHistoryAmount(amount: number): string {
    return this.bouncebackService.formatCurrency(
      amount, 
      this.companyService.selectedCurrency.CurrencyCode
    );
  }

  /**
   * Get CSS class for history item type
   */
  getHistoryItemClass(type: string): string {
    switch (type) {
      case 'earned':
        return 'history-earned';
      case 'redeemed':
        return 'history-redeemed';
      case 'expired':
        return 'history-expired';
      default:
        return '';
    }
  }

  /**
   * Get icon for history item type
   */
  getHistoryItemIcon(type: string): string {
    switch (type) {
      case 'earned':
        return 'add_circle';
      case 'redeemed':
        return 'remove_circle';
      case 'expired':
        return 'schedule';
      default:
        return 'info';
    }
  }

  /**
   * Track by function for history items
   */
  trackByOrderId(index: number, item: BounceBackHistory): string {
    return item.orderId;
  }
}
